import {
  getShoppingBasketFromLocalStorage,
  handleShoppingBasketUpdate,
  parseBasketJson,
} from '../../common/hooks/useShoppingBasket';
import {
  type BasketItem,
  BasketItemSource,
  type OfferItem,
  type OfferItemAddon,
  type ShoppingBasketType,
} from '../../common/types/shoppingBasket';
import { getEffectiveCharges, getM2MOpeningFee, getSelfServiceSubscriptionPrice } from '../../common/utils/addOnUtils';
import { deepEqual } from '../../common/utils/objectUtils';
import { compareStringArrays } from '../../common/utils/stringUtils';
import { CommercialProductType } from '../../generated/api/commercialProductType';
import { OnlineModelCategory } from '../../generated/api/onlineModelCategory';
import { isOmaElisa } from '../../selfservice/common';
import { dispatchBasketItemAdded } from '../HeaderShoppingCart/basketPopupEvents';
import {
  createOfferItemAddons,
  getAllOfferAddonCodes,
  getM2MProductName,
  isMobileM2MProduct,
} from './shoppingBasketHelpers';
import type { CartItemContainer, SelectedAddOnBundle } from '../../components/ProductDetails/ProductDetails';
import type { CommercialProduct } from '../../generated/api/commercialProduct';
import type { Offer } from '../../generated/api/offer';
import type { OnlineModel } from '../../generated/api/onlineModel';

const generateUniqueId = (existingItems: ShoppingBasketType): string => {
  const newId = crypto.randomUUID();
  if (existingItems.items?.find(existingItem => existingItem.id === newId)) {
    return generateUniqueId(existingItems);
  } else {
    return newId;
  }
};

/**
 * @deprecated Flattens `OfferItem.addOns` array and loses per-product addon tracking. Use the structured array directly.
 */
export const getAddonsForOfferItem = (offerItem?: OfferItem): string[] => {
  if (!offerItem?.addOns) {
    return [];
  }
  const allAddons = offerItem.addOns.flatMap(addon => addon.addOnCodes ?? []);
  return [...new Set(allAddons)];
};

const getUniqueAddonConfigurations = (addOns?: OfferItemAddon[]): string[][] => {
  const normalized = (addOns ?? []).map(a => [...(a.addOnCodes ?? [])].sort()).sort(compareStringArrays);
  return normalized.filter((config, index, self) => self.findIndex(c => deepEqual(c, config)) === index);
};

export const areItemsEqualInBasket = (newBasketItem: BasketItem, existingBasketItem: BasketItem) => {
  return (
    existingBasketItem.guid === newBasketItem.guid &&
    existingBasketItem.offer.guid === newBasketItem.offer.guid &&
    existingBasketItem.offer.commercialProductGuid === newBasketItem.offer.commercialProductGuid &&
    deepEqual(
      getUniqueAddonConfigurations(existingBasketItem.offer.addOns),
      getUniqueAddonConfigurations(newBasketItem.offer.addOns)
    )
  );
};

export interface AddToShoppingBasketProps {
  basketItem: Omit<BasketItem, 'id'>;
  isLoggedIn: boolean;
  voucherCode?: string;
}

export const getBasketItemSource = () => {
  return isOmaElisa(window.location.pathname) ? BasketItemSource.OMAELISA : BasketItemSource.PUBLIC_PAGE;
};

export const addToBasket = ({ basketItem, isLoggedIn, voucherCode }: AddToShoppingBasketProps) => {
  const shoppingBasketJson: ShoppingBasketType = parseBasketJson(getShoppingBasketFromLocalStorage());
  const newBasketItem = {
    ...basketItem,
    source: getBasketItemSource(),
    id: generateUniqueId(shoppingBasketJson),
  };
  const existingBasketItem = shoppingBasketJson.items?.find(existingItem =>
    areItemsEqualInBasket(newBasketItem, existingItem)
  );
  if (existingBasketItem) {
    existingBasketItem.quantity += newBasketItem.quantity;
    // Extend addOns array to match quantity - each unit needs its own addon entry
    existingBasketItem.offer.addOns = [
      ...(existingBasketItem.offer.addOns ?? []),
      ...(newBasketItem.offer.addOns ?? []),
    ];
  } else {
    shoppingBasketJson.items = [...(shoppingBasketJson.items || []), newBasketItem];
  }
  if (voucherCode) {
    shoppingBasketJson.vouchers = Array.from(new Set([...(shoppingBasketJson.vouchers || []), voucherCode]));
  }
  void handleShoppingBasketUpdate(JSON.stringify(shoppingBasketJson), isLoggedIn);

  const addedItemId = existingBasketItem ? existingBasketItem.id : newBasketItem.id;
  dispatchBasketItemAdded({ id: addedItemId, quantity: newBasketItem.quantity, guid: newBasketItem.guid });
};

export const addToShoppingBasket = (
  onlineModel: OnlineModel,
  offer: Offer,
  selectedCommercialProduct: CommercialProduct,
  isLoggedIn: boolean,
  quantity: number,
  selectedAddOns: string[],
  voucherCode?: string,
  addOnAttributes?: { [addOnCode: string]: { [key: string]: string } },
  cardMonthlyPrice?: number,
  cardOneTimePrice?: number
) => {
  const isSalesProduct =
    onlineModel.category === OnlineModelCategory.SALES_PRODUCT ||
    selectedCommercialProduct.productType === CommercialProductType.SALES_PRODUCT;
  const isMobileM2M = isMobileM2MProduct(onlineModel);

  const spCpWithOnetimeCharge = isSalesProduct
    ? offer.commercialProducts.find(cp => getEffectiveCharges(cp).oneTimeCharge > 0)
    : undefined;
  const spCpWithMonthlyCharge = isSalesProduct
    ? offer.commercialProducts.find(cp => getEffectiveCharges(cp).monthlyRecurringCharge > 0)
    : undefined;

  const cpForPricing = isSalesProduct ? spCpWithMonthlyCharge || selectedCommercialProduct : selectedCommercialProduct;
  const cpCharges = getEffectiveCharges(cpForPricing);

  const mandatoryAddon = isMobileM2M
    ? selectedCommercialProduct.associatedAddOns?.find(addon =>
        selectedCommercialProduct.addOnAssociations?.some(
          assoc => assoc.addOnCode === addon.addOnCode && assoc.addOnPurpose === 'MANDATORY'
        )
      )
    : undefined;

  // SELF_SERVICE pricing (Voice/MBB). Returns undefined for SALES_PRODUCT and M2M.
  const selfServicePricing = !isSalesProduct
    ? getSelfServiceSubscriptionPrice(selectedCommercialProduct, onlineModel)
    : undefined;

  // Opening fee: M2M from addOns[], SELF_SERVICE from pricing util, other from CP
  const baseOneTimeCharge = isSalesProduct
    ? (cardOneTimePrice ?? (spCpWithOnetimeCharge ? getEffectiveCharges(spCpWithOnetimeCharge).oneTimeCharge : 0))
    : cpCharges.oneTimeCharge;

  const oneTimePriceInCents = (() => {
    if (isMobileM2M) {
      return getM2MOpeningFee(selectedCommercialProduct.oneTimeCharge, onlineModel);
    }
    if (selfServicePricing) {
      return selfServicePricing.oneTimeCharge;
    }
    return baseOneTimeCharge;
  })();

  // Monthly: M2M = CP + mandatory addon, SELF_SERVICE = pricing util, other = CP
  const monthlyRecurringCharge = {
    periodicPriceInCents: (() => {
      if (isMobileM2M && mandatoryAddon) {
        return cpCharges.monthlyRecurringCharge + (mandatoryAddon.addOnMonthlyRecurringCharge ?? 0);
      }
      if (selfServicePricing) {
        return selfServicePricing.monthlyRecurringCharge;
      }
      if (isSalesProduct && cardMonthlyPrice !== undefined) {
        return cardMonthlyPrice;
      }
      return cpCharges.monthlyRecurringCharge;
    })(),
    payments: cpForPricing.payments,
    period: cpForPricing.billingPeriod ?? 1,
  };

  // Addons: SELF_SERVICE = bundled + user-selected, M2M = mandatory + user-selected, other = user-selected
  const addOnsForBasket = (() => {
    // M2M: mandatory variant addon + user selections
    if (isMobileM2M && mandatoryAddon) {
      return [...selectedAddOns, mandatoryAddon.addOnCode];
    }

    // SELF_SERVICE: merge ALL offer addons (isOfferAddOn=true) with user selections.
    // Salesforce requires all offer addons in the order payload regardless of addOnPurpose.
    // OPTIONAL addons are visible in basket UI (isBundledOfferAddon returns false for them).
    // Group max constraints are applied at order time by getValidatedAddOns.
    if (onlineModel.category === OnlineModelCategory.SELF_SERVICE) {
      const offerAddonCodes = getAllOfferAddonCodes(selectedCommercialProduct);
      const mergedAddons = [...new Set([...offerAddonCodes, ...selectedAddOns])];
      return mergedAddons;
    }

    // Devices and SALES_PRODUCT: user selections only
    return selectedAddOns;
  })();

  const basketItemName =
    isMobileM2M && mandatoryAddon
      ? getM2MProductName(onlineModel, mandatoryAddon.addOnCode, offer.offerName)
      : offer.offerName;

  addToBasket({
    isLoggedIn,
    basketItem: {
      guid: onlineModel.onlineModelCode,
      imageUrl: offer.images?.[0] ?? onlineModel.listingImage ?? '',
      name: basketItemName,
      offer: {
        guid: offer.offerCode,
        oneTimePriceInCents,
        periodicPrice: monthlyRecurringCharge,
        addOns: createOfferItemAddons(quantity, addOnsForBasket, addOnAttributes),
        commercialProductGuid:
          (isSalesProduct && spCpWithMonthlyCharge?.commercialProductCode) ||
          selectedCommercialProduct.commercialProductCode ||
          '',
      },
      pageUrl: onlineModel.pagePath || window.location.pathname,
      quantity,
    },
    voucherCode,
  });
};

export const addToShoppingBasketFromContainer = (
  cartItemContainer: CartItemContainer,
  sendShoppingBasketToApi: boolean
) => {
  addToShoppingBasket(
    cartItemContainer.onlineModel,
    cartItemContainer.offer,
    cartItemContainer.commercialProduct,
    sendShoppingBasketToApi,
    cartItemContainer.quantity,
    cartItemContainer.selectedAddOnBundles.map((a: SelectedAddOnBundle) => a.addOnCode)
  );
};

/** Add a SELF_SERVICE subscription to basket. Delegates to addToShoppingBasket. */
export const addSubscriptionToShoppingBasket = (
  onlineModel: OnlineModel,
  offer: Offer,
  selectedCommercialProduct: CommercialProduct,
  isLoggedIn: boolean,
  quantity: number,
  selectedAddOns: string[],
  _associations?: unknown,
  voucherCode?: string,
  addOnAttributes?: { [addOnCode: string]: { [key: string]: string } }
) => {
  addToShoppingBasket(
    onlineModel,
    offer,
    selectedCommercialProduct,
    isLoggedIn,
    quantity,
    selectedAddOns,
    voucherCode,
    addOnAttributes
  );
};
